import { PiecePropertyMap } from '@activepieces/pieces-framework';
import {
  Action,
  FlowOperationType,
  Trigger,
  buildDynamicPropertiesKey,
} from '@activepieces/shared';
import { useMutation } from '@tanstack/react-query';
import deepEqual from 'deep-equal';
import { t } from 'i18next';
import { RefreshCw } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';

import { useStepSettingsContext } from '../step-settings/step-settings-context';

import { AutoPropertiesFormComponent } from './auto-properties-form';
import { formUtils, useSavedDynamicProperties } from './form-utils';

import {
  nextPendingFlowWriteKey,
  useBuilderStateContext,
} from '@/app/builder/builder-hooks';
import { SelectUtilButton } from '@/components/custom/select-util-button';
import { SkeletonList } from '@/components/ui/skeleton';
import { piecesApi } from '@/features/pieces/lib/pieces-api';

type DynamicPropertiesProps = {
  refreshers: string[];
  propertyName: string;
  disabled: boolean;
};
const DynamicProperties = React.memo((props: DynamicPropertiesProps) => {
  const [
    flowVersion,
    applyOperation,
    selectedStep,
    readonly,
    registerPendingFlowWrite,
    resolvePendingFlowWrite,
  ] = useBuilderStateContext((state) => [
    state.flowVersion,
    state.applyOperation,
    state.selectedStep,
    state.readonly,
    state.registerPendingFlowWrite,
    state.resolvePendingFlowWrite,
  ]);
  const form = useFormContext<Action | Trigger>();
  const { updateFormSchema } = useStepSettingsContext();
  const isFirstRender = useRef(true);
  const previousValues = useRef<undefined | unknown[]>(undefined);

  const [propertyMap, setPropertyMap] = useState<PiecePropertyMap | undefined>(
    undefined,
  );
  const newRefreshers = [...props.refreshers, 'auth'];

  const { mutate, isPending } = useMutation<
    PiecePropertyMap,
    Error,
    { input: Record<string, unknown>; writeKey: string }
  >({
    // The pending write resolves in this finally, not in a mutate() callback:
    // react-query drops those when the component unmounts first, and a step
    // change during a resolution would leave the key pending for good.
    mutationFn: async ({ input, writeKey }) => {
      try {
        return await resolveProperties(input);
      } finally {
        resolvePendingFlowWrite(writeKey);
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const resolveProperties = async (
    input: Record<string, unknown>,
  ): Promise<PiecePropertyMap> => {
    const { settings } = form.getValues();
    const actionOrTriggerName = settings.actionName ?? settings.triggerName;
    const { pieceName, pieceVersion, pieceType, packageType } = settings;
    const result = piecesApi.options<PiecePropertyMap>({
      pieceName,
      pieceVersion,
      pieceType,
      packageType,
      propertyName: props.propertyName,
      actionOrTriggerName,
      input,
      flowVersionId: flowVersion.id,
      flowId: flowVersion.flowId,
    });
    const dynamicKey = buildDynamicPropertiesKey({
      flowId: flowVersion.flowId,
      pieceName,
      stepName: String(selectedStep),
      actionOrTriggerName,
      propName: props.propertyName,
    });
    applyOperation(
      {
        type: FlowOperationType.UPDATE_DYNAMIC_PROPERTIES_VALUES,
        request: { dynamicPropertiesValues: { [dynamicKey]: await result } },
      },
      () => console.error('Operation failed'),
    );
    return result;
  };

  /* eslint-disable react-hooks/rules-of-hooks */
  const refresherValues = newRefreshers.map((refresher) =>
    useWatch({
      name: `settings.input.${refresher}` as const,
      control: form.control,
    }),
  );
  /* eslint-enable react-hooks/rules-of-hooks */

  const refresh = () => {
    // Création d'un objet input qui contiendra les valeurs des rafraîchisseurs
    const input: Record<string, unknown> = {};
    newRefreshers.forEach((refresher, index) => {
      input[refresher] = refresherValues[index];
    });

    // Resolution is an HTTP round trip, then a flow write: signal it so anything
    // reading the flow server-side (AI builder, test run) waits for it
    const writeKey = nextPendingFlowWriteKey(
      `dynamic-props:${props.propertyName}`,
    );
    registerPendingFlowWrite(writeKey);

    mutate(
      { input, writeKey },
      {
        onSuccess: (response) => {
          // Récupération de la valeur actuelle du formulaire
          const currentValue = form.getValues(
            `settings.input.${props.propertyName}`,
          );
          // Calcul de la valeur par défaut en fonction de la réponse et de la valeur actuelle
          const defaultValue = formUtils.getDefaultValueForStep(
            response,
            currentValue ?? {},
          );
          // Mise à jour de la carte des propriétés
          setPropertyMap(response);
          // Mise à jour du schéma du formulaire
          updateFormSchema(`settings.input.${props.propertyName}`, response);

          // Mise à jour de la valeur dans le formulaire avec validation
          form.setValue(`settings.input.${props.propertyName}`, defaultValue, {
            shouldValidate: true,
            shouldDirty: true,
          });
        },
      },
    );
  };

  useSavedDynamicProperties({
    propertyName: props.propertyName,
    onLoad: (value) => {
      setPropertyMap(value as PiecePropertyMap);
    },
  });

  useDeepCompareEffectNoCheck(() => {
    const input: Record<string, unknown> = {};
    newRefreshers.forEach((refresher, index) => {
      input[refresher] = refresherValues[index];
    });

    if (
      !isFirstRender.current &&
      !deepEqual(previousValues.current, refresherValues)
    ) {
      // the field state won't be cleared if you only unset the parent prop value
      if (propertyMap) {
        Object.keys(propertyMap).forEach((childPropName) => {
          form.setValue(
            `settings.input.${props.propertyName}.${childPropName}` as const,
            null,
            {
              shouldValidate: true,
            },
          );
        });
      }
      form.setValue(`settings.input.${props.propertyName}` as const, null, {
        shouldValidate: true,
      });
      refresh();
    }

    previousValues.current = refresherValues;
    isFirstRender.current = false;
  }, refresherValues);

  return (
    <div className="space-y-2">
      {!isPending && !readonly && (
        <div className="flex justify-end">
          <SelectUtilButton
            tooltipText={t('Refresh')}
            onClick={refresh}
            Icon={RefreshCw}
          ></SelectUtilButton>
        </div>
      )}
      {isPending && (
        <div className="space-y-3">
          <SkeletonList numberOfItems={3} className="h-7"></SkeletonList>
        </div>
      )}
      {!isPending && propertyMap && (
        <AutoPropertiesFormComponent
          prefixValue={`settings.input.${props.propertyName}`}
          props={propertyMap}
          useMentionTextInput={true}
          disabled={props.disabled}
          allowDynamicValues={true}
        ></AutoPropertiesFormComponent>
      )}
    </div>
  );
});

DynamicProperties.displayName = 'DynamicProperties';
export { DynamicProperties };
